// Page scrolling. Two quirks shape every helper here:
//   - `html, body { height: 100% }` (modern.css) makes `body` the scroll root,
//     so `window.scrollY` stays 0 and `window.scrollTo` is a no-op. Read every
//     plausible root, and write them all.
//   - Bootstrap's `:root { scroll-behavior: smooth }` animates programmatic
//     scrolls, and overlapping animations cancel each other. Force `instant`.

/** Height of the fixed header; anchored content is placed just below it. */
export const HEADER_OFFSET = 74;

export const getScrollTop = (): number =>
    Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, document.body.scrollTop || 0);

/** Jump (never animate) to `top`. */
export const scrollToY = (top: number): void => {
    window.scrollTo({ top, left: 0, behavior: 'instant' as ScrollBehavior });
    document.documentElement.scrollTop = top;
    document.body.scrollTop = top;
};

export const scrollToTop = (): void => scrollToY(0);

/** The scroll offset that puts `el` just below the fixed header. */
const offsetOf = (el: Element): number =>
    Math.max(0, Math.round(el.getBoundingClientRect().top + getScrollTop() - HEADER_OFFSET));

/** Jump so `el` sits just below the fixed header. */
export const scrollToElement = (el: Element): void => scrollToY(offsetOf(el));

/**
 * Scroll so `el` sits just below the fixed header, and keep it there while the
 * page settles. Everything above it can still move: cover images without
 * dimensions finish loading, isotope lays a grid out, and the router scrolls to
 * the top once a link navigation's transition completes (after the page has
 * already rendered). So rather than pinning once, re-pin every frame for
 * `settleMs` — stopping as soon as the user scrolls, touches, or presses a key,
 * so we never fight them.
 */
export const pinScrollTo = (el: Element, settleMs = 2500): void => {
    const pin = () => {
        // Detached, or hidden (e.g. filtered out by isotope): its rect is
        // meaningless, so leave the scroll alone.
        if (!el.isConnected || !el.getClientRects().length) return;
        const top = offsetOf(el);
        if (Math.abs(top - getScrollTop()) >= 2) scrollToY(top);
    };

    const userEvents = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const;
    let stopped = false;
    const stop = () => {
        stopped = true;
        for (const type of userEvents) window.removeEventListener(type, stop);
    };
    for (const type of userEvents) window.addEventListener(type, stop, { passive: true });

    const until = performance.now() + settleMs;
    const tick = () => {
        if (stopped) return;
        pin();
        if (performance.now() < until) requestAnimationFrame(tick);
        else stop();
    };
    tick();
};
